import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Fragment, useEffect, useRef } from 'react';
import { EmptyState } from '../common/EmptyState';
import { CardRow } from '../common/CardTable/CardRow';
import { CardCell } from '../common/CardTable/CardCell';
import { COLUMN_HEADERS, COLUMN_WIDTHS } from './columnConfig';
import { flattenLocales, summarizeLocaleTokens, MAX_LOCALES_SHOWN } from './localeSummary';

const StyledTableContainer = styled(TableContainer)(() => ({
    height: '100%',
    overflowX: 'visible',
}));

const LoadingMoreContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
}));

interface SetsConfigSearchResultsProps {
    results: Models.IngestConfigApi.SetsConfigs.SetsConfigSummary[];
    searchTerm: string;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
}

const formatModifiedDate = (modifiedDate: string): string => new Date(modifiedDate).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
});

// Show the hover breakdown whenever the cell holds more than one culture, so a collapsed region
// label (e.g. "North America") or a +N overflow can always be expanded to its groups and cultures.
const shouldShowLocaleTooltip = (
    cultureGroups: Models.IngestConfigApi.SetsConfigs.CultureGroup[],
): boolean => flattenLocales(cultureGroups).length > 1;

const renderLocaleTooltip = (
    cultureGroups: Models.IngestConfigApi.SetsConfigs.CultureGroup[],
): JSX.Element => (
    <Box>
        {cultureGroups.map((group, index) => (
            <Typography
                // eslint-disable-next-line react/no-array-index-key
                component="div"
                key={group.name ?? index}
                variant="caption"
            >
                {group.name ? `${group.name}: ` : ''}
                {group.cultures.join(', ')}
            </Typography>
        ))}
    </Box>
);

// Renders the compact locale label: region tokens (e.g. "North America") in bold to read as an aggregate,
// culture codes plain, comma separated, with a "+N" overflow.
const renderLocaleLabel = (cultureGroups: Models.IngestConfigApi.SetsConfigs.CultureGroup[]): JSX.Element => {
    const tokens = summarizeLocaleTokens(cultureGroups);

    if (tokens.length === 0) {
        return <span>—</span>;
    }

    const shown = tokens.slice(0, MAX_LOCALES_SHOWN);
    const remaining = tokens.length - MAX_LOCALES_SHOWN;

    return (
        <span>
            {shown.map((token, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={`${token.type}-${token.label}-${index}`}>
                    {index > 0 && ', '}
                    {token.type === 'region'
                        ? <Box component="span" sx={{ fontWeight: 600 }}>{token.label}</Box>
                        : token.label}
                </Fragment>
            ))}
            {remaining > 0 && ` +${remaining}`}
        </span>
    );
};

const renderLocalesCell = (
    cultureGroups: Models.IngestConfigApi.SetsConfigs.CultureGroup[],
): JSX.Element => {
    const label = renderLocaleLabel(cultureGroups);

    if (!shouldShowLocaleTooltip(cultureGroups)) {
        return label;
    }

    return (
        <Tooltip arrow title={renderLocaleTooltip(cultureGroups)}>
            {label}
        </Tooltip>
    );
};

const renderStatusCell = (isEnabled: boolean): string => (isEnabled ? 'Enabled' : 'Disabled');

export const SetsConfigSearchResults = ({
    results,
    searchTerm,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
}: SetsConfigSearchResultsProps): JSX.Element => {
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel) {
            return undefined;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        });

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    if (results.length === 0) {
        return (
            <EmptyState>
                <Typography gutterBottom align="center" variant="h6">
                    {searchTerm ? 'No Results Found' : 'No Sets Configs Yet'}
                </Typography>
                <Typography align="center" variant="body1">
                    {searchTerm
                        ? 'Try adjusting your search criteria'
                        : 'No sets configs exist yet. Create one to get started.'}
                </Typography>
            </EmptyState>
        );
    }

    return (
        <>
            <StyledTableContainer>
                <Table stickyHeader sx={{ pb: 8 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" width={COLUMN_WIDTHS.name}>{COLUMN_HEADERS.name}</TableCell>
                            <TableCell align="left" width={COLUMN_WIDTHS.productKey}>{COLUMN_HEADERS.productKey}</TableCell>
                            <TableCell align="left" width={COLUMN_WIDTHS.locale}>{COLUMN_HEADERS.locale}</TableCell>
                            <TableCell align="left" width={COLUMN_WIDTHS.status}>{COLUMN_HEADERS.status}</TableCell>
                            <TableCell align="left" width={COLUMN_WIDTHS.modifiedDate}>{COLUMN_HEADERS.modifiedDate}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((setsConfig) => (
                            <CardRow
                                hover
                                component={Link}
                                key={setsConfig.id}
                                to={`/sets-config/${encodeURIComponent(setsConfig.productKey)}`}
                            >
                                <CardCell align="left" width={COLUMN_WIDTHS.name}>{setsConfig.productName}</CardCell>
                                <CardCell align="left" width={COLUMN_WIDTHS.productKey}>{setsConfig.productKey}</CardCell>
                                <CardCell align="left" width={COLUMN_WIDTHS.locale}>
                                    {renderLocalesCell(setsConfig.cultureGroups)}
                                </CardCell>
                                <CardCell align="left" width={COLUMN_WIDTHS.status}>
                                    {renderStatusCell(setsConfig.isEnabled)}
                                </CardCell>
                                <CardCell align="left" width={COLUMN_WIDTHS.modifiedDate}>{formatModifiedDate(setsConfig.modifiedDate)}</CardCell>
                            </CardRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            {isFetchingNextPage && (
                <LoadingMoreContainer>
                    <CircularProgress size={24} />
                </LoadingMoreContainer>
            )}
            <Box ref={sentinelRef} sx={{ height: 1 }} />
        </>
    );
};
